import { useMemo, useCallback } from "react";
import { Col, Row, Form } from "reactstrap";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useAppSelector } from "../../../../ReduxToolkit/Hooks";
import { closeModal } from "../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";

import {
  additional_manager,
  manager,
} from "../../../../utils/Constant";
import { DynamicFieldRenderer } from "../../../../CommonElements/DynamicFieldRenderer";
import { useUpdateAttendanceCorrectionMutation } from "../../../../ReduxToolkit/api/attendance";
import { logErrors } from "../../../../utils/logErrors";
import {
  IAttendanceCorrectionFormsProps,
  AttendanceCorrectionForms,
} from "../../types";
import { attendanceSchema } from "../../validationSchema/attendanceSchema";
import { attendanceCorrectionFormFields } from "../../fields";
import FormFooter from "../../../../CommonElements/FormFooter";
import IconComponent from "../../../../CommonElements/IconComponent";
import { showApiResultAlert } from "../../../../utils/AlertManager/showApiAlert";
import { dateConversion } from "../../../../utils/dateUtils";
import { Calendar, Clock, LogIn, LogOut, User, Users } from "react-feather";
import { formatReportDate } from "../../../../utils/reportDateUtils";
import "../../attendance.scss";

const isMissingAttendanceTime = (time?: string | null) => {
  if (!time) return true;
  const normalized = time.trim().toLowerCase();
  return ["", "-", "na", "n/a", "null", "undefined"].includes(normalized);
};

const getLockedEditTime = (attendance: IAttendanceCorrectionFormsProps["attendance"]) => {
  const isInTimeMissing = isMissingAttendanceTime(attendance.in_time);
  const isOutTimeMissing = isMissingAttendanceTime(attendance.out_time);

  if (isInTimeMissing && isOutTimeMissing) return "2";
  if (isInTimeMissing) return "0";
  if (isOutTimeMissing) return "1";

  return "2";
};

const AttendanceCorrectionForm = ({
  attendance,
  endDate,
  startDate,
  activeYear,
  ccNames,
  toNames,
  dispatch,
  clearCCNames,
  clearToNames,
  onCorrectionSuccess,
}: IAttendanceCorrectionFormsProps) => {
  const { data: assignedManagerData } = useAppSelector(
    ({ employeeAssignedManagerDetails }) => employeeAssignedManagerDetails
  );
  const [
    updateAttendanceCorrection,
    { isLoading, reset: resetUpdateCorrectionForm },
  ] = useUpdateAttendanceCorrectionMutation();

  const lockedEditTime = useMemo(
    () => getLockedEditTime(attendance),
    [attendance]
  );

  // ===== Helpers =====
  const getApprovalIds = useCallback((): number[] => {
    const approvalIds: number[] = [];
    if (!assignedManagerData) return approvalIds;
    const {
      hr,
      manager,
      additional_manager: addManager,
    } = assignedManagerData?.data;
    if (hr?.id) approvalIds.push(hr.id);
    manager?.forEach(({ id }) => id && approvalIds.push(id));
    addManager?.forEach(({ id }) => id && approvalIds.push(id));
    return approvalIds;
  }, [assignedManagerData]);

  const getEmailList = useCallback(
    (field: "manager" | "additional_manager"): string[] =>
      assignedManagerData?.data?.[field]?.map((i) => i.email) ?? [],
    [assignedManagerData?.data]
  );

  // ===== Default Values =====
  const defaultValues = useMemo(
    () => ({
      emp_name: attendance.emp_name,
      date: attendance.date,
      in_time: attendance.in_time?.slice(0, 5) || "",
      out_time: attendance.out_time?.slice(0, 5) || "",
      remark_all: attendance.remark_all,
      manager: toNames?.map((d) => d.name).join(",") || "",
      cc: ccNames?.map((item) => item.name).join(", ") || "",
      reason: "",
      editTime: lockedEditTime,
    }),
    [attendance, toNames, ccNames, lockedEditTime]
  );

  // ===== RHF Setup =====
  const methods = useForm<AttendanceCorrectionForms>({
    resolver: yupResolver(attendanceSchema) as any,
    defaultValues,
    values: defaultValues,
  });

  const { handleSubmit, reset, watch } = methods;
  const values = watch();

  const handleReset = () => {
    clearCCNames();
    clearToNames();
    reset();
    resetUpdateCorrectionForm();
  };

  const updateAttendanceCorrectionAlertMeta = {
    title: `Attendance Correction Request – ${
      attendance.emp_name
    } (${dateConversion(attendance.date, true)})`,
    source: "attendanceCorrectionModal",
  };

  // ===== Submit =====
  const onSubmit = async (formValues: AttendanceCorrectionForms) => {
    if (!attendance?.user_id)
      return logErrors("User ID missing in attendance object");

    const payload = {
      ...formValues,
      user_id: attendance.user_id.toString(),
      in_time_edited: ["0", "2"].includes(formValues.editTime),
      out_time_edited: ["1", "2"].includes(formValues.editTime),
      manager_email: getEmailList(manager),
      additional_manager_email: getEmailList(additional_manager),
      approval_from_ids: getApprovalIds(),
    };

    const attendanceCorrectionResponse = await updateAttendanceCorrection(
      payload
    );
    const isCorrectionSuccess =
      "data" in attendanceCorrectionResponse &&
      Boolean(attendanceCorrectionResponse.data?.success);

    if (isCorrectionSuccess) {
      onCorrectionSuccess?.();
    }

    showApiResultAlert(
      attendanceCorrectionResponse,
      updateAttendanceCorrectionAlertMeta,
      isCorrectionSuccess ? handleReset : undefined,
      dispatch,
      true
    );
  };

  // ===== Render =====
  const renderFields = (names: string[]) => <Row>{attendanceCorrectionFormFields.filter((field) => names.includes(field.name)).map((field) => {
    if (field.showCondition && !field.showCondition(values)) return null;
    return <Col xs={field.name === "reason" ? 12 : 6} md={field.col ?? 6} key={field.name}>{DynamicFieldRenderer(field, field.options ?? [])}</Col>;
  })}</Row>;
  return (
    <FormProvider {...methods}>
      <Form className="theme-form attendance-correction-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="correction-form-content">
          <p className="correction-intro">Update your attendance time and share the reason for approval.</p>
          <div className="correction-employee">
            <span className="correction-employee-icon"><User size={20} aria-hidden="true" /></span>
            <div><span>Employee</span><strong>{attendance.emp_name || "–"}</strong></div>
            <div className="correction-date"><Calendar size={14} aria-hidden="true" />{formatReportDate(attendance.date)}</div>
          </div>
          <fieldset className="correction-time-choice">
            <legend><Clock size={15} aria-hidden="true" />Which time would you like to edit?</legend>
            <div className="correction-time-options">
              {[{ value: "0", label: "In Time", icon: LogIn }, { value: "1", label: "Out Time", icon: LogOut }, { value: "2", label: "Both Times", icon: Clock }].map(({ value, label, icon: Icon }) => <label key={value} className={values.editTime === value ? "selected" : ""}>
                <input type="radio" value={value} {...methods.register("editTime")} /><Icon size={15} aria-hidden="true" /><span>{label}</span>
              </label>)}
            </div>
            {methods.formState.errors.editTime && <span className="text-danger small">{methods.formState.errors.editTime.message}</span>}
          </fieldset>
          <div className="correction-time-fields">{renderFields(["in_time", "out_time"])}</div>
          <div className="correction-reason-fields">{renderFields(["reason"])}</div>
          <div className="correction-recipients"><h6><Users size={15} aria-hidden="true" />Approval recipients</h6>{renderFields(["manager", "cc"])}</div>
        </div>
        <div className="correction-form-footer">
        <FormFooter
          onClose={() => dispatch(closeModal())}
          isShowCloseBtn={false}
          saveBtnLg="5"
          saveClassName="w-100"
          isSaveBtnDisabled={isLoading}
          saveLabel={
            isLoading ? (
              <>
                <IconComponent className="fa fa-spinner fa-spin" /> Loading....
              </>
            ) : (
              "Send request"
            )
          }
        />
        </div>
      </Form>
    </FormProvider>
  );
};

export default AttendanceCorrectionForm;
